// BSCR workspace — orchestrates the report builder (sections, evidence, registers).
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Loader2, RefreshCw, Sparkles, FileText, AlertTriangle, Paperclip,
    ListChecks, ClipboardList, Building2, ShieldAlert, FileClock, Grid3x3, Layers, Download, Images,
    GripVertical, List, FolderTree, MoreVertical, Plus, Pencil, Trash2, ArrowUp, ArrowDown, RotateCcw,
    CornerDownRight, Check, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import Pill from "@/components/Pill";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
    AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { SPECIAL, RECORD_STATUS_TONE, StatusDot } from "./shared";
import SectionEditor from "./SectionEditor";
import EvidencePanel from "./EvidencePanel";
import ImagePanel from "./ImagePanel";
import MissingInfo from "./MissingInfo";
import RiskMatrix from "./RiskMatrix";
import Scenarios from "./Scenarios";
import { Mor, VersionHistory } from "./MorVersion";

const SPECIAL_ICON = { B: FileClock, S: ClipboardList, T: ShieldAlert, V: Grid3x3, W: Layers };

export default function BscrWorkspace() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [sel, setSel] = useState("A");
    const [busy, setBusy] = useState(false);
    const [showReset, setShowReset] = useState(false);

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/bsc/${id}/workspace`); setData(data); }
        catch (e) { setError(formatApiError(e.response?.data?.detail) || "Could not load BSCR"); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    async function initialise() {
        setBusy(true);
        try { await api.post(`/bsc/${id}/initialise`); toast.success("BSCR workspace created"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }
    async function prefillAll() {
        setBusy(true);
        try { const { data } = await api.post(`/bsc/${id}/prefill`, {}); toast.success(`Pre-filled ${data.applied} field(s) across the report`); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }
    async function exportPdf() {
        setBusy(true);
        try {
            const res = await api.get(`/bsc/${id}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(res, `BSCR-${data?.record?.reference || id}.pdf`);
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("BSCR PDF exported");
        } catch (e) {
            const detail = e.response?.data instanceof Blob ? "Could not export PDF" : formatApiError(e.response?.data?.detail);
            toast.error(detail || "Could not export PDF");
        } finally { setBusy(false); }
    }

    async function resetStructure() {
        setBusy(true);
        try { await api.post(`/bsc/${id}/sections/reset`); toast.success("Structure reset to the Priority Risk canonical tree"); setSel("A"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
        finally { setBusy(false); setShowReset(false); }
    }

    if (error) return <div className="p-8 text-center text-[#B33A2E]">{error}</div>;
    if (!data) return <div className="p-12 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading BSCR…</div>;

    const { record: rec, summary, names, can_edit: canEdit, nav } = data;

    return (
        <div data-testid="bscr-workspace" className="space-y-5 pb-10">
            {/* Header */}
            <button data-testid="bscr-back" onClick={() => navigate("/app/bsc")} className="inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: "var(--brand-accent,#C75A1B)" }}>
                <ArrowLeft size={15} /> Building Safety Cases
            </button>

            <div className="rounded-2xl border border-[#E5E0DA] bg-white p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-block pb-0.5 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent,#C75A1B)", color: "#6F6A64" }}>Building Safety Case Report</span>
                            <Pill tone={RECORD_STATUS_TONE[rec.status] || "neutral"}>{rec.status}</Pill>
                            <span className="text-[11px] font-mono text-[#9A968F]">{rec.reference} · v{rec.report_version}</span>
                        </div>
                        <h1 className="text-[22px] font-bold text-[#1F1F1F] mt-1.5">{rec.report_title || rec.title}</h1>
                        <div className="text-[12.5px] text-[#6F6A64] mt-1 flex items-center gap-2 flex-wrap">
                            <Building2 size={13} /> {names.building || "—"} · {names.client} {names.site ? `· ${names.site}` : ""}
                            {rec.hrb_reference && <span className="font-mono text-[11px]">· HRB {rec.hrb_reference}</span>}
                        </div>
                        <div className="text-[11.5px] text-[#9A968F] mt-1">Prepared by {rec.prepared_by || "—"} · Reviewed by {rec.reviewed_by || "—"} · Approved by {rec.approved_by || "—"} · {rec.report_date || ""}</div>
                    </div>
                    {rec.bscr_initialised && (
                        <div className="flex items-center gap-2">
                            {canEdit && (
                                <button data-testid="bscr-prefill-all" onClick={prefillAll} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#E5E0DA] text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">
                                    {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Pre-fill all from system
                                </button>
                            )}
                            {canEdit && (
                                <button data-testid="bscr-reset-structure" onClick={() => setShowReset(true)} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-[#E5E0DA] text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">
                                    <RotateCcw size={14} /> Reset structure
                                </button>
                            )}
                            <button data-testid="bscr-export-pdf" onClick={exportPdf} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export PDF
                            </button>
                        </div>
                    )}
                </div>

                {rec.bscr_initialised && (
                    <div className="grid gap-2.5 mt-4" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(132px,1fr))" }}>
                        <Kpi testid="bscr-kpi-completion" icon={FileText} label="Sections approved" value={`${summary.section_completion_pct}%`} sub={`${summary.sections_approved}/${summary.sections_total}`} />
                        <Kpi testid="bscr-kpi-fields" icon={Layers} label="Fields populated" value={`${summary.field_fill_pct}%`} />
                        <Kpi testid="bscr-kpi-evidence" icon={Paperclip} label="Evidence coverage" value={`${summary.evidence_pct}%`} />
                        <Kpi testid="bscr-kpi-missing" icon={ClipboardList} label="Missing info (open)" value={summary.missing_open} tone={summary.missing_open ? "amber" : "green"} />
                        <Kpi testid="bscr-kpi-actions" icon={ListChecks} label="Open actions" value={summary.open_actions} tone="blue" />
                        <Kpi testid="bscr-kpi-overdue" icon={AlertTriangle} label="Overdue actions" value={summary.overdue_actions} tone={summary.overdue_actions ? "red" : "neutral"} />
                        <Kpi testid="bscr-kpi-scenarios" icon={Layers} label="Risk scenarios" value={summary.scenarios_total} />
                    </div>
                )}
            </div>

            {!rec.bscr_initialised ? (
                <div className="rounded-2xl border border-dashed border-[#D5CEC6] bg-[#FCFBF9] p-10 text-center">
                    <Sparkles className="mx-auto mb-3 text-[var(--brand-accent,#C75A1B)]" size={26} />
                    <h3 className="text-[16px] font-bold text-[#1F1F1F]">Build the BSCR workspace</h3>
                    <p className="text-[13px] text-[#6F6A64] mt-1 max-w-md mx-auto">Create the 23 standard report sections, risk matrix, scenarios and registers — then pre-fill from the Golden Thread, Fire Safety, BSA, residents and more.</p>
                    {canEdit ? (
                        <button data-testid="bscr-initialise" onClick={initialise} disabled={busy} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Initialise BSCR
                        </button>
                    ) : <p className="text-[12.5px] italic text-[#9A968F] mt-3">This report has not been started yet.</p>}
                </div>
            ) : (
                <div className="grid grid-cols-12 gap-5 items-start">
                    {/* Left nav — numbered section tree */}
                    <SectionNav rid={rec.id} nav={nav} sel={sel} setSel={setSel} canEdit={canEdit} reload={load} />

                    {/* Center + right */}
                    <SectionArea rec={rec} sel={sel} nav={nav} canEdit={canEdit} reload={load} onSelect={setSel} boilerplate={data.boilerplate || { fields: {}, narratives: {} }} />
                </div>
            )}

            <AlertDialog open={showReset} onOpenChange={setShowReset}>
                <AlertDialogContent data-testid="bscr-reset-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset to canonical structure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This restores the Priority Risk default section tree and numbering. Your field values, statuses, narratives and evidence are preserved for the standard sections, but any custom sections, custom headings and re-ordering will be removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bscr-reset-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="bscr-reset-confirm" onClick={resetStructure}>Reset structure</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ---------- Left navigation: numbered, hierarchical, drag-to-reorder + edit ----------
function SectionNav({ rid, nav, sel, setSel, canEdit, reload }) {
    const [dragCode, setDragCode] = useState(null);
    const [overCode, setOverCode] = useState(null);
    const [renaming, setRenaming] = useState(null);   // {code, value}
    const [delTarget, setDelTarget] = useState(null); // {code, title, childCount}
    const [working, setWorking] = useState(false);
    const dragRef = useRef(null);
    const overRef = useRef(null);

    const draggable = (n) => canEdit && !n.is_scenario && !n.is_contents && !n.is_container && n.kind !== "front_matter";
    const editable = (n) => canEdit && !n.is_scenario && !n.is_contents;
    const real = nav.filter((n) => !n.is_scenario);

    async function call(fn, okMsg) {
        setWorking(true);
        try { const r = await fn(); if (okMsg) toast.success(okMsg); await reload(); return r; }
        catch (e) {
            if (e.response?.status === 409) toast.error("Has approved content — confirm to delete.");
            else toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
        } finally { setWorking(false); }
    }

    async function addSection() {
        const r = await call(() => api.post(`/bsc/${rid}/sections`, { title: "New section" }), "Section added");
        const code = r?.data?.code; if (code) { setSel(code); setRenaming({ code, value: "New section" }); }
    }
    async function addSub(parent) {
        const r = await call(() => api.post(`/bsc/${rid}/sections`, { title: "New sub-section", parent_code: parent }), "Sub-section added");
        const code = r?.data?.code; if (code) { setSel(code); setRenaming({ code, value: "New sub-section" }); }
    }
    async function saveRename() {
        const { code, value } = renaming; setRenaming(null);
        await call(() => api.patch(`/bsc/${rid}/sections/${code}`, { title: value || "Untitled" }), "Renamed");
    }
    async function changeKind(code, kind) {
        await call(() => api.patch(`/bsc/${rid}/sections/${code}`, { section_kind: kind, clear_parent: kind !== "numbered" }), "Section type changed");
    }
    async function doDelete(children) {
        const code = delTarget.code; setDelTarget(null);
        await call(() => api.delete(`/bsc/${rid}/sections/${code}?children=${children}&force=true`), "Section deleted");
        if (sel === code) setSel("A");
    }

    // Move a section (with its children) up/down among its siblings -> reflows numbering.
    async function move(code, dir) {
        const codes = real.map((n) => n.code);
        const entry = real.find((n) => n.code === code);
        const start = codes.indexOf(code);
        let end = start;
        for (let i = start + 1; i < real.length; i++) { if (real[i].parent_code === code) end = i; else break; }
        const block = codes.slice(start, end + 1);
        const sib = (i) => real[i] && real[i].parent_code === entry.parent_code && real[i].kind === entry.kind;
        let next;
        if (dir === "up") {
            let p = start - 1; while (p >= 0 && !sib(p)) p--;
            if (p < 0) return;
            const before = codes.slice(0, p), prevBlock = codes.slice(p, start), after = codes.slice(end + 1);
            next = [...before, ...block, ...prevBlock, ...after];
        } else {
            let q = end + 1; if (!sib(q)) return;
            let qEnd = q; for (let i = q + 1; i < real.length; i++) { if (real[i].parent_code === real[q].code) qEnd = i; else break; }
            const before = codes.slice(0, start), nextBlock = codes.slice(q, qEnd + 1), after = codes.slice(qEnd + 1);
            next = [...before, ...nextBlock, ...block, ...after];
        }
        await call(() => api.post(`/bsc/${rid}/sections/reorder`, { ordered_codes: next }), "Section moved");
    }

    async function commitReorder() {
        const from = dragRef.current, to = overRef.current;
        setDragCode(null); setOverCode(null); dragRef.current = null; overRef.current = null;
        if (!from || !to || from === to) return;
        const dragE = real.find((n) => n.code === from);
        const overE = real.find((n) => n.code === to);
        if (!dragE || !overE || dragE.parent_code !== overE.parent_code || dragE.kind !== overE.kind) return;
        const codes = real.map((n) => n.code);
        codes.splice(codes.indexOf(from), 1);
        codes.splice(codes.indexOf(to), 0, from);
        try { await api.post(`/bsc/${rid}/sections/reorder`, { ordered_codes: codes }); await reload(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed"); }
    }

    function startDrag(e, code) {
        e.preventDefault(); e.stopPropagation();
        setDragCode(code); dragRef.current = code; overRef.current = code;
        const mv = (ev) => {
            const el = document.elementFromPoint(ev.clientX, ev.clientY)?.closest("[data-navcode]");
            const c = el?.getAttribute("data-navcode");
            if (c) { overRef.current = c; setOverCode(c); }
        };
        const up = () => {
            window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up);
            document.body.style.userSelect = ""; commitReorder();
        };
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
    }

    return (
        <nav data-testid="bscr-section-nav" className="col-span-12 lg:col-span-3 rounded-2xl border border-[#E5E0DA] bg-white p-2 lg:sticky lg:top-4 max-h-[82vh] overflow-y-auto">
            {nav.map((n) => {
                const code = n.code;
                const selCode = n.is_scenario ? "W" : code;
                const Icon = SPECIAL_ICON[code] || (n.is_contents ? List : n.kind === "annex" ? Images : n.is_container ? FolderTree : null);
                const isOver = overCode === code && dragCode && dragCode !== code;
                const isRenaming = renaming?.code === code;
                return (
                    <div key={code} data-testid={`bscr-nav-${code}`} data-navcode={code}
                        className={`group w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition
                            ${sel === selCode ? "bg-[#FBEFE5]" : "hover:bg-[#F6F4F0]"}
                            ${dragCode === code ? "opacity-40" : ""} ${isOver ? "ring-2 ring-[var(--brand-accent,#C75A1B)]" : ""}`}
                        style={{ paddingLeft: `${6 + n.depth * 16}px` }}>
                        {draggable(n) && (
                            <span onPointerDown={(e) => startDrag(e, code)} data-testid={`bscr-nav-grip-${code}`}
                                className="cursor-grab text-[#C4BEB6] hover:text-[#6F6A64] touch-none shrink-0" title="Drag to reorder">
                                <GripVertical size={13} />
                            </span>
                        )}
                        <span className={`grid place-items-center min-w-[24px] h-5 px-1 rounded text-[10px] font-bold shrink-0
                            ${sel === selCode ? "text-white" : "text-[#6F6A64] bg-[#F0EDE8]"}`}
                            style={sel === selCode ? { background: "var(--brand-accent,#C75A1B)" } : undefined}>
                            {n.number || (Icon ? <Icon size={11} /> : "•")}
                        </span>
                        {isRenaming ? (
                            <input data-testid={`bscr-rename-input-${code}`} autoFocus value={renaming.value}
                                onChange={(e) => setRenaming({ code, value: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenaming(null); }}
                                onBlur={saveRename}
                                className="flex-1 text-[12px] border border-[var(--brand-accent,#C75A1B)] rounded px-1 py-0.5 outline-none" />
                        ) : (
                            <button onClick={() => setSel(selCode)}
                                className={`flex-1 text-left text-[12px] truncate ${n.is_container ? "font-bold text-[#1F1F1F]" : "font-medium text-[#2b2b2b]"} ${n.is_scenario ? "italic text-[#6F6A64]" : ""}`}>{n.title}</button>
                        )}
                        {!n.is_contents && !n.is_container && n.kind !== "annex" && !n.is_scenario && <StatusDot status={n.status} />}
                        {editable(n) && !isRenaming && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button data-testid={`bscr-nav-menu-${code}`} disabled={working}
                                        className="shrink-0 text-[#C4BEB6] hover:text-[#1F1F1F] opacity-0 group-hover:opacity-100 focus:opacity-100">
                                        <MoreVertical size={14} />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="text-[12.5px]" onCloseAutoFocus={(e) => e.preventDefault()}>
                                    <DropdownMenuItem data-testid={`bscr-menu-add-sub-${code}`} onClick={() => addSub(code)}><CornerDownRight size={13} className="mr-2" /> Add sub-section</DropdownMenuItem>
                                    <DropdownMenuItem data-testid={`bscr-menu-rename-${code}`} onClick={() => setRenaming({ code, value: n.title })}><Pencil size={13} className="mr-2" /> Rename</DropdownMenuItem>
                                    <DropdownMenuItem data-testid={`bscr-menu-up-${code}`} onClick={() => move(code, "up")}><ArrowUp size={13} className="mr-2" /> Move up</DropdownMenuItem>
                                    <DropdownMenuItem data-testid={`bscr-menu-down-${code}`} onClick={() => move(code, "down")}><ArrowDown size={13} className="mr-2" /> Move down</DropdownMenuItem>
                                    <DropdownMenuSub>
                                        <DropdownMenuSubTrigger><Layers size={13} className="mr-2" /> Section type</DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="text-[12.5px]">
                                            <DropdownMenuItem onClick={() => changeKind(code, "numbered")}>Numbered</DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => changeKind(code, "annex")}>Annex</DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => changeKind(code, "front_matter")}>Front matter</DropdownMenuItem>
                                        </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem data-testid={`bscr-menu-delete-${code}`} className="text-[#B33A2E] focus:text-[#B33A2E]"
                                        onClick={() => setDelTarget({ code, title: n.title, childCount: real.filter((x) => x.parent_code === code).length })}>
                                        <Trash2 size={13} className="mr-2" /> Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                );
            })}
            {canEdit && (
                <button data-testid="bscr-add-section" onClick={addSection} disabled={working}
                    className="w-full mt-1.5 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-dashed border-[#D5CEC6] text-[12px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0] hover:border-[var(--brand-accent,#C75A1B)]">
                    <Plus size={14} /> Add section
                </button>
            )}

            <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
                <AlertDialogContent data-testid="bscr-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete “{delTarget?.title}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {delTarget?.childCount > 0
                                ? `This section has ${delTarget.childCount} sub-section(s). Delete them too, or keep them and move them up a level?`
                                : "This will remove the section and its content from the report. Numbering will reflow."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bscr-delete-cancel">Cancel</AlertDialogCancel>
                        {delTarget?.childCount > 0 ? (
                            <>
                                <button data-testid="bscr-delete-reparent" onClick={() => doDelete("reparent")}
                                    className="px-3 py-2 rounded-lg border border-[#E5E0DA] text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">Keep sub-sections</button>
                                <AlertDialogAction data-testid="bscr-delete-children" onClick={() => doDelete("delete")} className="bg-[#B33A2E] hover:bg-[#962f25]">Delete all</AlertDialogAction>
                            </>
                        ) : (
                            <AlertDialogAction data-testid="bscr-delete-confirm" onClick={() => doDelete("reparent")} className="bg-[#B33A2E] hover:bg-[#962f25]">Delete</AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </nav>
    );
}

function SectionArea({ rec, sel, nav, canEdit, reload, onSelect, boilerplate }) {
    const rid = rec.id;
    const navEntry = nav.find((n) => n.code === sel);

    if (sel === "CONTENTS") return <Wide><ContentsPage nav={nav} onSelect={onSelect} /></Wide>;

    if (navEntry?.is_container) {
        const children = nav.filter((n) => n.parent_code === sel && !n.is_scenario);
        return (
            <Wide>
                <div data-testid={`bscr-container-${sel}`} className="space-y-4">
                    <div>
                        <h2 className="text-[18px] font-bold text-[#1F1F1F] flex items-center gap-2">
                            <span className="text-[var(--brand-accent,#C75A1B)] font-display">{navEntry.number}</span> {navEntry.title}
                        </h2>
                        <p className="text-[12.5px] text-[#6F6A64] mt-0.5">This is a grouping section — open a sub-section below to edit it.</p>
                    </div>
                    <div className="grid gap-2">
                        {children.map((c) => (
                            <button key={c.code} data-testid={`bscr-child-${c.code}`} onClick={() => onSelect(c.code)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E5E0DA] hover:bg-[#F6F4F0] text-left transition">
                                <span className="grid place-items-center min-w-[34px] h-7 px-1.5 rounded-lg text-[12px] font-bold text-[#6F6A64] bg-[#F0EDE8]">{c.number}</span>
                                <span className="flex-1 text-[13.5px] font-semibold text-[#1F1F1F]">{c.title}</span>
                                <StatusDot status={c.status} />
                            </button>
                        ))}
                    </div>
                </div>
            </Wide>
        );
    }

    if (sel === "ANNEX" || sel === "ANNEX2" || sel === "ANNEX3") {
        const titles = { ANNEX: "Annex 1 — Building Plans", ANNEX2: "Annex 2 — Emergency Plans", ANNEX3: "Annex 3 — Responsibilities Flow Chart" };
        const subs = {
            ANNEX: "Upload building plans, floor plans, maps and drawings. Appended as a dedicated annex at the end of the exported report.",
            ANNEX2: "Upload emergency and evacuation plans for this building.",
            ANNEX3: "Upload the accountability / responsibilities flow chart.",
        };
        return (
            <Wide>
                <div data-testid={`bscr-annex-${sel}`} className="space-y-3">
                    <div>
                        <h2 className="text-[18px] font-bold text-[#1F1F1F] flex items-center gap-2"><Images size={18} className="text-[var(--brand-accent,#C75A1B)]" /> {titles[sel]}</h2>
                        <p className="text-[12.5px] text-[#6F6A64]">{subs[sel]}</p>
                    </div>
                    <ImagePanel rid={rid} sectionCode={sel} images={rec.images} canEdit={canEdit} onChange={reload} title="Drawings & plans" />
                </div>
            </Wide>
        );
    }

    const sec = rec.bscr_sections.find((s) => s.code === sel);
    if (!sec) return null;

    if (sel === "S") return <Wide><MissingInfo rid={rid} items={rec.missing_information} sections={rec.bscr_sections} canEdit={canEdit} onChange={reload} /></Wide>;
    if (sel === "T") return <Wide><Mor rid={rid} mor={rec.mor || {}} categories={rec.mor?.reportable_categories?.length ? rec.mor.reportable_categories : MOR_CATS} canEdit={canEdit} onChange={reload} boilerplate={boilerplate} /></Wide>;
    if (sel === "V") return <Wide><RiskMatrix rid={rid} matrix={rec.risk_matrix} canEdit={canEdit} onChange={reload} /></Wide>;
    if (sel === "W") return <Wide><Scenarios rid={rid} scenarios={rec.scenarios} matrix={rec.risk_matrix} canEdit={canEdit} onChange={reload} /></Wide>;
    if (sel === "B") return <Wide><VersionHistory rid={rid} record={rec} canEdit={canEdit} onChange={reload} /></Wide>;

    return (
        <>
            <div className="col-span-12 lg:col-span-6 rounded-2xl border border-[#E5E0DA] bg-white p-5">
                <SectionEditor rid={rid} section={sec} number={navEntry?.number} images={rec.images} canEdit={canEdit} onChange={reload} boilerplate={boilerplate} />
            </div>
            <aside className="col-span-12 lg:col-span-3 rounded-2xl border border-[#E5E0DA] bg-white p-4 lg:sticky lg:top-4">
                <EvidencePanel rid={rid} code={sec.code} evidence={sec.evidence || []} canEdit={canEdit} onChange={reload} />
            </aside>
        </>
    );
}

function ContentsPage({ nav, onSelect }) {
    return (
        <div data-testid="bscr-contents-page" className="space-y-3">
            <div>
                <h2 className="text-[18px] font-bold text-[#1F1F1F] flex items-center gap-2"><List size={18} className="text-[var(--brand-accent,#C75A1B)]" /> Contents</h2>
                <p className="text-[12.5px] text-[#6F6A64]">Auto-generated from the section tree. Numbers and page references reflow when sections are added or reordered.</p>
            </div>
            <div className="rounded-xl border border-[#EFEAE4] divide-y divide-[#F0EDE8]">
                {nav.filter((n) => !n.is_contents).map((n) => (
                    <button key={n.code} data-testid={`bscr-toc-${n.code}`} onClick={() => onSelect(n.is_scenario ? "W" : n.code)}
                        className="w-full flex items-baseline gap-3 px-3 py-2 hover:bg-[#F6F4F0] text-left transition"
                        style={{ paddingLeft: `${12 + n.depth * 20}px` }}>
                        <span className={`font-mono text-[11.5px] min-w-[52px] ${n.number ? "text-[var(--brand-accent,#C75A1B)] font-bold" : "text-[#C4BEB6]"}`}>{n.number || "—"}</span>
                        <span className={`flex-1 text-[13px] ${n.is_container ? "font-bold text-[#1F1F1F]" : n.is_scenario ? "italic text-[#6F6A64]" : "text-[#2b2b2b]"}`}>{n.title}</span>
                        <span className="text-[10.5px] text-[#C4BEB6] font-mono">p. —</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

const Wide = ({ children }) => <div className="col-span-12 lg:col-span-9 rounded-2xl border border-[#E5E0DA] bg-white p-5">{children}</div>;

const MOR_CATS = [
    "Structural failure or collapse risk", "Spread of fire or smoke", "Failure of a fire safety measure",
    "Death or serious injury (building safety related)", "Risk requiring evacuation",
    "Unsafe escape route", "Other reportable safety occurrence",
];

const KPI_TONE = { amber: "#C68A1C", red: "#B33A2E", green: "#3F7D3A", blue: "#2A5DB0", neutral: "#6F6A64" };
function Kpi({ testid, icon: Icon, label, value, sub, tone = "neutral" }) {
    return (
        <div data-testid={testid} className="rounded-xl border border-[#EFEAE4] bg-[#FCFBF9] p-3">
            <Icon size={14} style={{ color: KPI_TONE[tone] }} />
            <div className="font-display font-bold text-[20px] leading-none text-[#1F1F1F] mt-2">{value}</div>
            <div className="text-[10.5px] mt-1 font-medium text-[#6F6A64]">{label}{sub ? ` · ${sub}` : ""}</div>
        </div>
    );
}
